import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";

export class FeeScheduleTemplateFilterParameters {
    query?: string;
    entityId: string;
    creatorUserId?: string;
    name?: string;
    createdDate?: Date;
    minCreatedDate?: Date;
    maxCreatedDate?: Date;
    tags?: string[];
    isArchived?: boolean;

    constructor(
        entityId: Guid,
        query?: string,
        isArchived?: boolean,
        filters?: { [key: string]: string },
    ) {
        this.entityId = entityId.toString();
        this.query = query;   
        this.isArchived = isArchived;
        this.creatorUserId = filters?.creatorUserId;
        this.name = filters?.name; 
        this.createdDate = filters?.createdDate ? new Date(filters.createdDate) : undefined;
        this.minCreatedDate = filters?.minCreatedDate ? new Date(filters.minCreatedDate) : undefined;
        this.maxCreatedDate = filters?.maxCreatedDate ? new Date(filters.maxCreatedDate) : undefined;
        this.tags = filters?.tags ? filters.tags.split(",").map(tag => tag.trim()) : undefined;
    }

    public asSearchParams(): URLSearchParams {
        const searchParams = new URLSearchParams();
        if (this.query) searchParams.append("query", this.query);
        if (this.entityId) searchParams.append("entityId", this.entityId);
        if (this.creatorUserId) searchParams.append("creatorUserId", this.creatorUserId);
        if (this.name) searchParams.append("name", this.name);
        if (this.createdDate) searchParams.append("createdDate", this.createdDate.toISOString());
        if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
        if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
        if (this.tags) {
            this.tags.forEach(tag => {
                searchParams.append("tags", tag);
            });
        }
        if (this.isArchived !== undefined) searchParams.append("isArchived", String(this.isArchived));
        return searchParams;
    }

    public isEqualTo(other: FeeScheduleTemplateFilterParameters): boolean {
        if (!other) return false;
        return this.query === other.query &&
            this.entityId === other.entityId &&
            this.creatorUserId === other.creatorUserId &&
            this.name === other.name &&
            this.createdDate?.getTime() === other.createdDate?.getTime() &&
            this.minCreatedDate?.getTime() === other.minCreatedDate?.getTime() &&
            this.maxCreatedDate?.getTime() === other.maxCreatedDate?.getTime() &&
            JSON.stringify(this.tags) === JSON.stringify(other.tags) &&
            this.isArchived === other.isArchived;
    }
}

export enum FeeScheduleTemplateOrderField {
    Name = "Name",
    CreatedDate = "CreatedDate",
    Tags = "Tags"
}

export class FeeScheduleTemplateOrderParameters {
    orderBy?: FeeScheduleTemplateOrderField;
    orderDir?: OrderDirection;

    constructor(
        orderBy?: FeeScheduleTemplateOrderField,
        orderDir?: OrderDirection
    ) {
        this.orderBy = orderBy;
        this.orderDir = orderDir;
    }

    public asSearchParams(): URLSearchParams {
        const params = new URLSearchParams();
        if (this.orderBy) params.append("orderBy", this.orderBy);
        if (this.orderDir) params.append("orderDir", this.orderDir);
        return params;
    }

    public isEqualTo(other: FeeScheduleTemplateOrderParameters): boolean {
        if (!other) return false;
        return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
    }
}


export default class FeeScheduleTemplateQuery {
    filterParameters: FeeScheduleTemplateFilterParameters;
    orderParameters: FeeScheduleTemplateOrderParameters;
    paginationParameters: PaginationParameters;

    constructor(
        filterParameters: FeeScheduleTemplateFilterParameters,
        orderParameters: FeeScheduleTemplateOrderParameters,
        paginationParameters: PaginationParameters
    ) {
        this.paginationParameters = paginationParameters;
        this.orderParameters = orderParameters;
        this.filterParameters = filterParameters;
    }

    public asSearchParams(): URLSearchParams {
        return new URLSearchParams([
            ...this.paginationParameters.asSearchParams(),
            ...this.orderParameters.asSearchParams(),
            ...this.filterParameters.asSearchParams()
        ]);
    }

    isEqualTo(other: FeeScheduleTemplateQuery): boolean {
        if (!other) {
            return false;
        }
        return this.filterParameters.isEqualTo((other.filterParameters)) &&
            this.orderParameters.isEqualTo((other.orderParameters)) &&
            this.paginationParameters.isEqualTo((other.paginationParameters));
    }
}